import axios from 'axios';
import type { HeroSectionApiPayload, HeroSectionContent } from '../constants/heroSection';
import { DashboardStats } from '../types/admin';

// Get the API URL from environment variables, with fallback for same-origin `/api`.
// In dev, webpack dev server proxies `/api` → backend (see `src/setupProxy.js`).
export const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  return '/api';
};

const API_URL = getApiUrl();

// Helper function to ensure path starts with /
const ensurePath = (path: string) => (path.startsWith('/') ? path : `/${path}`);

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
});

// Add request interceptor for authentication and path handling
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Ensure URL is properly formatted
  if (config.url) {
    config.url = ensurePath(config.url);
  }

  // Only set Content-Type to application/json if not sending FormData
  if (
    config.data &&
    typeof config.data === 'object' &&
    !(config.data instanceof FormData) &&
    !config.headers['Content-Type']
  ) {
    config.headers['Content-Type'] = 'application/json';
  }

  // Only log errors, not every request
  return config;
});

// Add response interceptor for handling token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If the error is 401 and we haven't tried to refresh the token yet
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== '/admin/auth/refresh'
    ) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Try to refresh the token
        const response = await api.post('/admin/auth/refresh', {
          refresh_token: refreshToken,
        });

        if (response.data?.success) {
          const { token, refresh_token } = response.data.data;

          // Update tokens in localStorage
          localStorage.setItem('token', token);
          localStorage.setItem('refresh_token', refresh_token);

          // Update the Authorization header
          originalRequest.headers.Authorization = `Bearer ${token}`;

          // Retry the original request
          return api(originalRequest);
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        // If refresh token is invalid or expired, clear tokens
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');

        // Let the AuthContext handle the navigation
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 429) {
      const config = error.config;
      config._retryCount = (config._retryCount || 0) + 1;
      if (config._retryCount <= 2) {
        const delay = config._retryCount * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
        return api(config);
      }
    }

    return Promise.reject(error);
  }
);

export interface ItinerarioAtividade {
  id: number;
  titulo: string;
  descricao?: string;
  horario?: string;
  ordem: number;
}

export interface ItinerarioDia {
  id: number;
  dia_numero: number;
  titulo: string;
  descricao: string;
  atividades: ItinerarioAtividade[];
}

export interface ItinerarioItem {
  id: number;
  viagem_id: number;
  dia_numero: number;
  titulo: string;
  descricao: string;
  horario: string | null;
  ordem: number;
  created_at: string;
  updated_at: string;
}

export interface Viagem {
  id: number;
  titulo: string;
  subtitulo: string;
  slug: string;
  descricao: string;
  data: string;
  duracao_dias: number;
  preco: number;
  suplemento_individual: number;
  transfer_porto?: number | null;
  status: string;
  categoria_id: number;
  imagem_url: string | null;
  localizacao: string;
  incluido: string[];
  nao_incluido: string[];
  opcional: { descricao: string; preco: number }[];
  featured: boolean;
  is_published?: boolean;
  ordem: number;
  notas: string;
  condicoes: string;
  acompanhamento: string;
  categoria_nome: string;
  categoria_slug: string;
  created_at: string;
  updated_at: string;
  pdf_url?: string;
  galeria?: {
    id: number;
    path: string;
    alt?: string;
    descricao?: string;
    is_featured: boolean;
    ordem: number;
  }[];
  metadados?: {
    meta_title?: string;
    meta_description?: string;
    canonical_url?: string;
    og_title?: string;
    og_description?: string;
    og_image?: string;
    og_url?: string;
    og_type?: string;
  };
  itinerario?: ItinerarioItem[];
}

export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

/** CMS sections: «A Nossa História», legal pages, etc. */
export interface AdminContentSection {
  id: number;
  section_key: string;
  title: string;
  subtitle?: string | null;
  content: string;
  created_at: string;
  updated_at: string;
}

/** Public CMS sections (no auth). */
export interface PublicContentSection {
  section_key: string;
  title: string;
  subtitle: string | null;
  content: string;
}

export interface Trip {
  id: number;
  titulo: string;
  subtitulo?: string;
  slug: string;
  descricao: string;
  preco: number;
  duracao_dias: number;
  categoria_id: number;
  categoria_nome?: string;
  categoria_slug?: string;
  imagem_url: string;
  localizacao: string;
  dificuldade?: string;
  data?: string;
  data_inicio?: string;
  data_fim?: string;
  suplemento_individual?: number;
  transfer_porto?: number | null;
  notas?: string;
  status?: 'brevemente' | 'ultimos-lugares' | 'ultimos_lugares' | 'disponivel' | 'esgotado';
  is_published?: boolean;
  created_at?: string;
  updated_at?: string;
  itinerario?: ItinerarioDia[];
  incluido?: string[];
  nao_incluido?: string[];
  opcional?: { descricao: string; preco: number }[];
  featured?: boolean;
  pdf_url?: string;
  condicoes?: string;
  galeria?: {
    id: number;
    path: string;
    alt?: string;
    descricao?: string;
    is_featured: boolean;
    ordem: number;
  }[];
  metadados?: {
    meta_title?: string;
    meta_description?: string;
    canonical_url?: string;
    og_title?: string;
    og_description?: string;
    og_image?: string;
    og_url?: string;
    og_type?: string;
  };
  acompanhamento?: string[] | string;
}

export interface TripsByMonth {
  [key: string]: Trip[];
}

export interface LoginResponse {
  success: boolean;
  data: {
    token: string;
    refresh_token: string;
    user: {
      id: number;
      nome: string;
      email: string;
      role: string;
    };
  };
}

export interface PaginationResponse {
  pagina_atual: number;
  total_paginas: number;
  total_items: number;
  items_por_pagina: number;
}

export interface PaginatedApiResponse<T> extends ApiResponse<T> {
  paginacao: PaginationResponse;
}

export interface TripParams {
  pagina?: number;
  limite?: number;
  categoria?: string;
  search?: string;
}

export interface TeamMember {
  id: number;
  nome: string;
  cargo: string;
  foto_url: string | null;
  bio: string | null;
  ordem: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export interface Testimonial {
  id: number;
  nome: string;
  rating: number;
  texto: string;
  ordem: number;
  ativo: boolean;
  viagem_id?: number | null;
  /** ISO date (YYYY-MM-DD) when the trip took place — optional. */
  data_viagem?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** Public category list with trip counts (GET /api/categorias). */
export interface CategoriaPublic {
  id: number;
  nome: string;
  slug: string;
  total_viagens?: number | string;
}

export interface Configuracoes {
  id: number;
  nome_empresa: string;
  email: string;
  telefone: string | null;
  morada: string | null;
  horario: string | null;
  redes_sociais: {
    facebook?: string;
    instagram?: string;
  } | null;
  email_settings: {
    smtp_host: string;
    smtp_port: number;
    smtp_secure: boolean;
  } | null;
  created_at: string;
  updated_at: string;
}

export interface CarouselItem {
  id: number;
  titulo: string;
  descricao: string;
  imagem_url: string;
  link_url: string;
  ordem: number;
  ativo: boolean;
  tipo: string;
  created_at: string;
  updated_at: string;
}

export interface Metadata {
  id: number;
  viagem_id: number;
  meta_title: string;
  meta_description: string;
  canonical_url: string;
  og_title: string;
  og_description: string;
  og_image: string;
  og_url: string;
  og_type: string;
  created_at: string;
  updated_at: string;
  viagem_titulo?: string;
  viagem_slug?: string;
}

// Trip endpoints
export const tripApi = {
  getAll: (params?: TripParams) =>
    api.get<PaginatedApiResponse<Trip[]>>('/viagens', { params }).then((response) => {
      if (!response.data || !response.data.data) {
        throw new Error('Invalid API response structure');
      }
      return response.data;
    }),

  getFeatured: (limit?: number) =>
    api
      .get<ApiResponse<Trip[]>>('/viagens/featured', limit ? { params: { limit } } : undefined)
      .then((response) => {
        if (!response.data || !response.data.data) {
          console.warn('Invalid API response structure for featured trips, returning empty array');
          return { success: true, data: [] };
        }
        return response.data;
      })
      .catch((error) => {
        console.error('Error fetching featured trips:', error);
        return { success: false, data: [] };
      }),

  getBySlug: (slug: string) =>
    api.get<Trip>(`/viagens/${slug}`).then((response) => {
      return {
        success: true,
        data: response.data,
      };
    }),

  getUpcoming: (limite = 6) =>
    api
      .get<Trip[]>('/viagens/upcoming', { params: { limite } })
      .then((response) => {
        // The upcoming endpoint returns a direct array, not wrapped in success/data
        if (Array.isArray(response.data)) {
          return response.data;
        }
        console.warn('Invalid API response structure for upcoming trips, returning empty array');
        return [];
      })
      .catch((error) => {
        // Handle network errors gracefully
        console.error('Error fetching upcoming trips:', error);
        return [];
      }),

  getByCategory: (categoria: string, params?: Omit<TripParams, 'categoria'>) =>
    api
      .get<PaginatedApiResponse<Trip[]>>('/viagens', {
        params: { ...params, categoria },
      })
      .then((response) => {
        if (!response.data || !response.data.data) {
          throw new Error('Invalid API response structure');
        }
        return response.data;
      }),

  search: (query: string, limite = 5) =>
    api
      .get<PaginatedApiResponse<Trip[]>>('/viagens', { params: { search: query, limite } })
      .then((response) => {
        if (!response.data || !response.data.data) {
          throw new Error('Invalid API response structure');
        }
        return response.data;
      }),
};

// Carousel endpoints
export const carouselApi = {
  getAll: (tipo?: string) =>
    api
      .get<ApiResponse<CarouselItem[]>>('/carrossel', tipo ? { params: { tipo } } : undefined)
      .then((response) => {
        if (!response.data || !response.data.data) {
          console.warn('Invalid API response structure for carousel, returning empty array');
          return { success: true, data: [] as CarouselItem[] };
        }
        return response.data;
      })
      .catch((error) => {
        console.error('Error fetching carousel:', error);
        return { success: false, data: [] as CarouselItem[] };
      }),
  getById: (id: number) => api.get<ApiResponse<CarouselItem>>(`/carrossel/${id}`),
  create: (data: FormData) => api.post<ApiResponse<CarouselItem>>('/carrossel', data),
  update: (id: number, data: FormData) =>
    api.put<ApiResponse<CarouselItem>>(`/carrossel/${id}`, data),
  delete: (id: number) => api.delete<ApiResponse<void>>(`/carrossel/${id}`),
};

// Statistics endpoints
export const statisticsApi = {
  getAll: () => api.get('/estatisticas'),
  getById: (id: number) => api.get(`/estatisticas/${id}`),
  create: (data: Record<string, unknown>) => api.post('/estatisticas', data),
  update: (id: number, data: Record<string, unknown>) => api.put(`/estatisticas/${id}`, data),
  delete: (id: number) => api.delete(`/estatisticas/${id}`),
};

// GA4 Analytics endpoints (admin only)
export type AnalyticsRange = '7d' | '30d';

export const analyticsApi = {
  getSummary: (range: AnalyticsRange) => api.get(`/admin/analytics/summary?range=${range}`),
  getViagens: (range: AnalyticsRange) => api.get(`/admin/analytics/viagens?range=${range}`),
  getViagem: (slug: string, range: AnalyticsRange) =>
    api.get(`/admin/analytics/viagens/${encodeURIComponent(slug)}?range=${range}`),
  getTrend: (range: AnalyticsRange) => api.get(`/admin/analytics/trend?range=${range}`),
};

// Contact form endpoints
export const contactApi = {
  submit: (data: {
    name: string;
    email: string;
    phone?: string;
    subject?: string;
    message: string;
  }) => {
    // Transform English field names to Portuguese for backend compatibility
    const transformedData = {
      nome: data.name,
      email: data.email,
      telefone: data.phone || null,
      assunto: data.subject || null,
      mensagem: data.message,
    };
    return api.post('/contacto', transformedData);
  },
};

// Newsletter endpoints
export const newsletterApi = {
  subscribe: (data: { name: string; email: string; contact?: string }) =>
    api.post('/newsletter', data),
};

// Private trips endpoints
export const privateTripsApi = {
  submit: (data: {
    nome_responsavel: string;
    tipo_grupo?: string;
    telefone?: string;
    email: string;
    destino: string;
    data_partida: string;
    data_chegada: string;
    datas_flexiveis?: string;
    num_participantes: string;
    descricao_programa?: string;
    tipologia_hotel?: string | string[];
    regime_pretendido?: string | string[];
    orcamento_pessoa?: string;
    observacoes?: string;
  }) => api.post('/private-trips', data),
};

// Testimonials endpoints (public)
export const testimonialsApi = {
  /** Global testimonials (homepage): API returns rows with no viagem_id. */
  getAll: () => api.get<Testimonial[]>('/testimonials').then((response) => response.data),
  /** Reviews shown on a specific trip detail page. */
  getByViagemId: (viagemId: number) =>
    api
      .get<Testimonial[]>('/testimonials', { params: { viagem_id: viagemId } })
      .then((response) => response.data),
};

/** Public categories + trip counts for mega-menu etc. */
export const categoriasApi = {
  getAll: () => api.get<CategoriaPublic[]>('/categorias').then((response) => response.data),
};

// Admin endpoints
export const adminApi = {
  // Authentication
  login: (credentials: { email: string; password: string }) =>
    api.post<LoginResponse>('/admin/auth/login', credentials),
  refreshToken: (refreshToken: string) =>
    api.post<ApiResponse<{ token: string; refresh_token: string }>>('/admin/auth/refresh', {
      refresh_token: refreshToken,
    }),

  // Dashboard
  dashboard: {
    getStats: () => api.get<ApiResponse<DashboardStats>>('/admin/dashboard/stats'),
  },

  // Trip management
  viagens: {
    getAll: (params?: {
      search?: string;
      status?: string;
      categoria_id?: string;
      /** When true, only trips with `featured` set (admin list filter). */
      featured?: boolean;
    }) =>
      api.get<ApiResponse<Viagem[]>>('/admin/viagens', {
        params: {
          search: params?.search,
          status: params?.status,
          categoria: params?.categoria_id, // Backend expects 'categoria' instead of 'categoria_id'
          ...(params?.featured ? { featured: 'true' } : {}),
        },
      }),
    getById: (id: number) => api.get<ApiResponse<Viagem>>(`/admin/viagens/${id}`),
    /** Preview trip by slug (includes drafts); auth required. Returns same shape as public trip. */
    getPreviewBySlug: (slug: string) => api.get<Trip>(`/admin/viagens/preview/${slug}`),
    create: (data: FormData) => api.post<ApiResponse<Viagem>>('/admin/viagens', data),
    update: (id: number, data: FormData) =>
      api.put<ApiResponse<Viagem>>(`/admin/viagens/${id}`, data),
    delete: (id: number) => api.delete(`/admin/viagens/${id}`),
    uploadImages: (id: number, data: FormData) => api.post(`/admin/viagens/${id}/galeria`, data),
    updateItinerary: (id: number, itinerario: ItinerarioDia[]) =>
      api.post(`/admin/viagens/${id}/itinerario`, { itinerario }),
    deleteGalleryImage: (viagemId: number, imageId: number) =>
      api.delete(`/admin/viagens/${viagemId}/galeria/${imageId}`),
    updateGalleryImage: (viagemId: number, imageId: number, data: FormData) =>
      api.put(`/admin/viagens/${viagemId}/galeria/${imageId}`, data),
    setFeaturedImage: (viagemId: number, imageId: number) =>
      api.put(`/admin/viagens/${viagemId}/galeria/${imageId}/featured`),
    uploadPdf: (id: number, data: FormData) => api.post(`/admin/viagens/${id}/pdf`, data),
    deletePdf: (id: number) => api.delete(`/admin/viagens/${id}/pdf`),
    togglePublish: (id: number, is_published: boolean) =>
      api.patch<ApiResponse<{ id: number; is_published: boolean; featured?: boolean }>>(
        `/admin/viagens/${id}/publish`,
        { is_published }
      ),
  },

  // Settings
  settings: {
    get: () => api.get('/admin/configuracoes'),
    update: (data: Record<string, unknown>) => api.put('/admin/configuracoes', data),
    getEmailTemplates: () => api.get('/admin/configuracoes/email-templates'),
    updateEmailTemplate: (id: number, data: Record<string, unknown>) =>
      api.put(`/admin/configuracoes/email-templates/${id}`, data),
  },

  // User management
  users: {
    getAll: () => api.get('/admin/configuracoes/users'),
    create: (data: Record<string, unknown>) => api.post('/admin/configuracoes/users', data),
    update: (id: number, data: Record<string, unknown>) =>
      api.put(`/admin/configuracoes/users/${id}`, data),
    delete: (id: number) => api.delete(`/admin/configuracoes/users/${id}`),
  },

  // Categories management
  categorias: {
    getAll: () =>
      api.get<
        ApiResponse<
          {
            id: number;
            nome: string;
            slug: string;
            total_viagens: number;
          }[]
        >
      >('/admin/categorias'),
    getById: (id: number) =>
      api.get<
        ApiResponse<{
          id: number;
          nome: string;
          slug: string;
        }>
      >(`/admin/categorias/${id}`),
    create: (data: { nome: string }) =>
      api.post<
        ApiResponse<{
          id: number;
          nome: string;
          slug: string;
        }>
      >('/admin/categorias', data),
    update: (id: number, data: { nome: string }) =>
      api.put<
        ApiResponse<{
          id: number;
          nome: string;
          slug: string;
        }>
      >(`/admin/categorias/${id}`, data),
    delete: (id: number) => api.delete(`/admin/categorias/${id}`),
  },

  configuracoes: {
    get: () => api.get<ApiResponse<Configuracoes>>('/admin/configuracoes'),
    update: (data: Partial<Configuracoes>) =>
      api.put<ApiResponse<Configuracoes>>('/admin/configuracoes', data),
  },

  team: {
    getAll: () => api.get<ApiResponse<TeamMember[]>>('/admin/team'),
    getById: (id: number) => api.get<ApiResponse<TeamMember>>(`/admin/team/${id}`),
    create: (data: FormData) => api.post<ApiResponse<TeamMember>>('/admin/team', data),
    update: (id: number, data: FormData) =>
      api.put<ApiResponse<TeamMember>>(`/admin/team/${id}`, data),
    delete: (id: number) => api.delete<ApiResponse<void>>(`/admin/team/${id}`),
  },

  testimonials: {
    getAll: () => api.get<ApiResponse<Testimonial[]>>('/admin/testimonials'),
    create: (data: Omit<Testimonial, 'id' | 'created_at' | 'updated_at'>) =>
      api.post<ApiResponse<Testimonial>>('/admin/testimonials', data),
    update: (id: number, data: Partial<Omit<Testimonial, 'id' | 'created_at' | 'updated_at'>>) =>
      api.put<ApiResponse<Testimonial>>(`/admin/testimonials/${id}`, data),
    delete: (id: number) => api.delete<ApiResponse<void>>(`/admin/testimonials/${id}`),
  },

  carrossel: {
    getAll: () => api.get<ApiResponse<CarouselItem[]>>('/admin/carrossel'),
    getById: (id: number) => api.get<ApiResponse<CarouselItem>>(`/admin/carrossel/${id}`),
    create: (data: FormData) => api.post<ApiResponse<CarouselItem>>('/admin/carrossel', data),
    update: (id: number, data: FormData) =>
      api.put<ApiResponse<CarouselItem>>(`/admin/carrossel/${id}`, data),
    delete: (id: number) => api.delete<ApiResponse<void>>(`/admin/carrossel/${id}`),
  },

  metadados: {
    getAll: () => api.get<ApiResponse<Metadata[]>>('/admin/metadados'),
    getById: (id: number) => api.get<ApiResponse<Metadata>>(`/admin/metadados/${id}`),
    create: (data: Omit<Metadata, 'id' | 'created_at' | 'updated_at'>) =>
      api.post<ApiResponse<Metadata>>('/admin/metadados', data),
    update: (id: number, data: Partial<Omit<Metadata, 'id' | 'created_at' | 'updated_at'>>) =>
      api.put<ApiResponse<Metadata>>(`/admin/metadados/${id}`, data),
    delete: (id: number) => api.delete<ApiResponse<void>>(`/admin/metadados/${id}`),
  },

  // Hero (home hero section)
  hero: {
    get: () => api.get<ApiResponse<HeroSectionApiPayload>>('/admin/hero'),
    update: (data: HeroSectionContent) => api.put<ApiResponse<void>>('/admin/hero', data),
  },

  favicon: {
    getStatus: () =>
      api.get<
        ApiResponse<{
          hasSource: boolean;
          sourcePath: string | null;
          updatedAt: string | null;
          hasLinkPreviewImage?: boolean;
          linkPreviewImagePath?: string | null;
          linkPreviewUpdatedAt?: string | null;
        }>
      >('/admin/favicon/status'),
    upload: (data: FormData) =>
      api.post<ApiResponse<{ path: string }>>('/admin/favicon/upload', data),
    generate: () =>
      api.post<ApiResponse<{ message: string; log?: string }>>('/admin/favicon/generate'),
  },

  // Featured destinations display config
  destaque: {
    get: () =>
      api.get<ApiResponse<{ layout: string; quantidade: number }>>('/admin/configuracoes/destaque'),
    update: (data: { layout: string; quantidade: number }) =>
      api.put<ApiResponse<{ layout: string; quantidade: number }>>(
        '/admin/configuracoes/destaque',
        data
      ),
  },

  content: {
    getSections: () => api.get<ApiResponse<AdminContentSection[]>>('/admin/content'),
    updateSection: (
      sectionKey: string,
      payload: { title: string; content: string; subtitle?: string | null }
    ) => api.put<ApiResponse<unknown>>(`/admin/content/${sectionKey}`, payload),
  },
};

/** Public content sections (About + legal pages metadata). */
export const contentApi = {
  getSections: async (): Promise<PublicContentSection[]> => {
    const response = await api.get<ApiResponse<PublicContentSection[]>>('/content/sections');
    const body = response.data;
    if (!body?.success || !Array.isArray(body.data)) {
      throw new Error('Resposta inválida ao carregar secções de conteúdo.');
    }
    return body.data;
  },

  getSection: async (sectionKey: string): Promise<PublicContentSection> => {
    const response = await api.get<ApiResponse<PublicContentSection>>(
      `/content/sections/${encodeURIComponent(sectionKey)}`
    );
    const body = response.data;
    if (!body?.success || body.data == null) {
      throw new Error('Resposta inválida ao carregar a página.');
    }
    return body.data;
  },
};

// Hero endpoint (public)
export const heroApi = {
  get: () =>
    api.get<ApiResponse<HeroSectionApiPayload>>('/hero').then((response) => {
      if (!response.data || !response.data.data) {
        throw new Error('Invalid API response structure');
      }
      return response.data;
    }),
};

// Featured destinations config (public)
export const destaqueApi = {
  get: () =>
    api
      .get<ApiResponse<{ layout: string; quantidade: number }>>('/configuracoes/destaque')
      .then((response) => {
        if (!response.data || !response.data.data) {
          return { layout: 'grid', quantidade: 6 };
        }
        return response.data.data;
      })
      .catch(() => ({ layout: 'grid', quantidade: 6 })),
};

// Calendar endpoint
export const calendarApi = {
  getTrips: () =>
    api.get<ApiResponse<TripsByMonth>>('/calendario').then((response) => {
      if (!response.data || !response.data.data) {
        throw new Error('Invalid API response structure');
      }
      return response.data;
    }),
};

export default api;
